import { CalendarCellProps } from '@/components/calendar/calendar.types';
import { secondsToTimeString } from '@/helpers/dateHelper';
import { merge } from '@/helpers/Utility';
import {
  FixedLeaveTypeId,
  UNKNOWN_LEAVE_TYPE_LABEL,
  UNKNOWN_LEAVE_TYPE_SURFACE_COLOR,
  UNKNOWN_LEAVE_TYPE_WRAPPER_COLOR
} from '@/services/availability/availability.types';
import { useAvailabilityTypes } from '@/services/availability/useAvailabilityTypes';
import { strings } from '@/services/translation/strings';
import { FC } from 'react';
import { twMerge } from 'tailwind-merge';
import { TeamMemberCalendarContextObject } from '../../team/teamMember/calendar/useTeamMemberCalendarEvents';
import essStrings from '../ess.translations';

const cellStyling = 'py-2 px-3 text-sm text-center w-56';

const formatRange = (label: string, start: number, end: number) =>
  `${label}: ${secondsToTimeString(start)} - ${secondsToTimeString(end)}`;

export const MyCalendarCalendarContent: FC<CalendarCellProps<TeamMemberCalendarContextObject>> = ({
  contextObject
}) => {
  const currentDate = new Date();
  currentDate.setHours(0, 0, 0, 0);

  const leaveTypes = useAvailabilityTypes();

  if (!contextObject) {
    return <div></div>;
  }

  const hasAvailabilityMismatch = () => {
    if (!contextObject.availableTimes || contextObject.availableTimes.length === 0) return false;

    return contextObject.availableTimes.some(
      at => at.start !== contextObject.patternStart || at.end !== contextObject.patternEnd
    );
  };

  // TODO keeping availability mismatch?
  const borderClass = hasAvailabilityMismatch() ? 'border border-dotted border-gray-500' : '';

  if (contextObject.shifts && contextObject.shifts.length > 0) {
    return (
      <div className="h-full flex justify-center items-center">
        {contextObject.shifts.map((shift, index) => (
          <div key={index} className="flex flex-row items-stretch space-x-2">
            <div
              className={twMerge('border-2 border-primary-500 text-primary-500 rounded-md bg-primary-100', cellStyling)}
            >
              <p className="font-semibold">{formatRange(strings.common.shift.toUpperCase(), shift.start, shift.end)}</p>
            </div>
            {shift.tasks && shift.tasks.length > 0 && (
              <div className={twMerge('bg-primary-500 text-white rounded-md w-14 text-xs flex items-center justify-center px-2')}>
                <p className='text-wrap text-center leading-tight'>{essStrings.myCalendar.tasks(shift.tasks.length)}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  }

  const leaveLookup = contextObject.leaveType
    ? leaveTypes.find(lt => lt.leaveTypeId === contextObject.leaveType)
    : undefined;
  const unknownLeaveType = contextObject.leaveType === FixedLeaveTypeId.UNKNOWN;

  if (leaveLookup || unknownLeaveType) {
    const colour = leaveLookup?.color;
    return (
      <div
        className="h-full flex justify-center items-center"
        style={{ backgroundColor: unknownLeaveType ? UNKNOWN_LEAVE_TYPE_WRAPPER_COLOR : `${colour}80` }}
      >
        <div
          className={twMerge(
            cellStyling,
            unknownLeaveType && 'border border-gray-300 bg-gray-200 text-gray-700',
            'rounded-md'
          )}
          style={{ backgroundColor: unknownLeaveType ? UNKNOWN_LEAVE_TYPE_SURFACE_COLOR : colour }}
        >
          {unknownLeaveType ? UNKNOWN_LEAVE_TYPE_LABEL : leaveLookup?.name.toUpperCase()}
        </div>
      </div>
    );
  }
  if (contextObject.availableTimes && contextObject.availableTimes.length > 0) {
    return (
      <div className="h-full flex justify-center items-center text-gray-500">
        {contextObject.availableTimes.map((shift, index) => (
          <div key={index} className={twMerge(borderClass, cellStyling)}>
            <p>{formatRange(strings.common.available.toUpperCase(), shift.start, shift.end)}</p>
          </div>
        ))}
      </div>
    );
  }

  const start = contextObject.patternStart ? secondsToTimeString(contextObject.patternStart) : strings.common.na;
  const end = contextObject.patternEnd ? secondsToTimeString(contextObject.patternEnd) : strings.common.na;
  return (
    <div
      className={merge(
        'relative h-full w-full flex items-center justify-center',
        contextObject.hoverOnly ? 'opacity-0 hover:opacity-100' : ''
      )}
    >
      <div className="w-14 h-14 rounded-md flex flex-col items-center justify-center">
        <span>
          {start} - {end}
        </span>
      </div>
    </div>
  );
};
